import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Input,
  FormControl,
  FormLabel,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItemOption,
  MenuOptionGroup,
  HStack,
  Stack,
  useToast,
  IconButton,
  Spinner,
  Tooltip,
  useColorMode,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Badge,
  Select,
  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
  Code,
  Skeleton,
  SkeletonText,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  FormErrorMessage,
  useBreakpointValue,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useClipboard,
  FormHelperText,
  Icon,
  Checkbox,
  VStack,
} from '@chakra-ui/react';
import { MdSettings, MdFilterList, MdRefresh, MdDownload, MdInfo, MdSearch, MdClear, MdDateRange, MdPhone, MdCall, MdCallEnd, MdPhoneInTalk, MdPhoneForwarded, MdAttachMoney, MdAccessTime, MdTrendingUp, MdTrendingDown, MdEqualizer } from 'react-icons/md';
import { ExternalLinkIcon, PhoneIcon, ArrowBackIcon, ArrowForwardIcon, DownloadIcon, ChevronDownIcon } from '@chakra-ui/icons';
import Navigation from '../components/Navigation';
import axios from '../utils/axiosConfig';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Helmet } from "react-helmet";
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { BILLING_COLUMNS, getDefaultVisibleColumns } from '../config/billingColumns';
import { useNavigate } from 'react-router-dom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const BillingPage = () => {
  const { colorMode } = useColorMode();
  const [filteredBillings, setFilteredBillings] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fromNumber, setFromNumber] = useState('');
  const [toNumber, setToNumber] = useState('');
  const [callSid, setCallSid] = useState('');
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalCalls: 0,
    totalCost: 0,
    averageDuration: 0,
    trend: 'up'
  });
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: 'Daily Cost',
        data: [],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.5)',
        tension: 0.1,
        fill: true
      }
    ]
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isRecordingOpen,
    onOpen: onRecordingOpen,
    onClose: onRecordingClose
  } = useDisclosure();
  const [selectedCall, setSelectedCall] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [recordingPlayerUrl, setRecordingPlayerUrl] = useState('');
  const [recordingLoadingCallSid, setRecordingLoadingCallSid] = useState('');
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const navigate = useNavigate();

  const allColumns = Object.values(BILLING_COLUMNS);

  const getAssistantId = (billing) => {
    if (!billing?.assistantId) return '';
    return typeof billing.assistantId === 'object' ? billing.assistantId._id : billing.assistantId;
  };

  const getAssistantName = (billing) => {
    if (!billing?.assistantId) return 'No assistant';
    return typeof billing.assistantId === 'object' ? billing.assistantId.name || 'Unnamed assistant' : 'Unknown assistant';
  };

  const openAssistant = (billing) => {
    const assistantId = getAssistantId(billing);
    if (assistantId) {
      navigate(`/assistant?assistantId=${assistantId}`);
    }
  };

  const getPreferences = async () => {
    try {
      const response = await axios.get(`/api/protected/preferences`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching preferences:', error);
      throw error;
    }
  };

  const updatePreferences = async (preferences) => {
    try {
      const preferencesToSend = {
        ...preferences,
        billingColumns: preferences.billingColumns ?
          (preferences.billingColumns instanceof Map ?
            Object.fromEntries(preferences.billingColumns) :
            preferences.billingColumns) :
          {}
      };
      const response = await axios.put(`/api/protected/preferences`, preferencesToSend);
      return response.data.data;
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };


  const exportToExcel = async () => {
    try {
      setIsExporting(true);

      // Build filters object from current filter states
      const filters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (fromNumber) filters.from = fromNumber;
      if (toNumber) filters.to = toNumber;
      if (callSid) filters.callSid = callSid;

      // Fetch all billings with current filters
      let allBillings = [];
      let page = 1;
      let hasMoreData = true;

      while (hasMoreData) {
        try {
          const params = new URLSearchParams({
            page: String(page),
            limit: '100',
            ...filters
          });

          const response = await axios.get(`/api/protected/billings?${params.toString()}`);
          const { billings, pagination } = response.data.data;

          if (billings && billings.length > 0) {
            allBillings = [...allBillings, ...billings];
            if (page >= pagination.totalPages) {
              hasMoreData = false;
            } else {
              page += 1;
            }
          } else {
            hasMoreData = false;
          }
        } catch (error) {
          console.error('Error fetching billings for export:', error);
          hasMoreData = false;
        }
      }

      if (allBillings.length === 0) {
        toast({
          title: 'No Data',
          description: 'There is no data to export with current filters',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Billing Records');

      // Define columns with proper headers and formatting
      worksheet.columns = [
        { header: 'Date', key: 'date', width: 20 },
        { header: 'Call ID', key: 'callId', width: 30 },
        { header: 'Assistant Name', key: 'assistantName', width: 28 },
        { header: 'Assistant ID', key: 'assistantId', width: 30 },
        { header: 'From', key: 'from', width: 15 },
        { header: 'To', key: 'to', width: 15 },
        { header: 'Duration (mins)', key: 'duration', width: 15 },
        { header: 'Charged Duration (mins)', key: 'chargedDuration', width: 20 },
        { header: 'Rate (SGD/min)', key: 'rate', width: 15 },
        { header: 'Total Cost (SGD)', key: 'totalCost', width: 15 },
        { header: 'Direction', key: 'direction', width: 12 }
      ];

      // Add data rows
      allBillings.forEach((record) => {
        worksheet.addRow({
          date: new Date(record.createdAt).toLocaleString(),
          callId: record.callSid,
          assistantName: getAssistantName(record),
          assistantId: getAssistantId(record),
          from: record.from,
          to: record.to,
          duration: (record.duration / 60000).toFixed(2),
          chargedDuration: Math.ceil(record.chargedDuration / 60000),
          rate: record.rate?.toFixed(2) || 0,
          totalCost: record.totalCost?.toFixed(2) || 0,
          direction: record.direction
        });
      });

      // Style the header row
      worksheet.getRow(1).font = { bold: true };
      worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };

      // Apply number formatting
      worksheet.getColumn('duration').numFmt = '0.00';
      worksheet.getColumn('chargedDuration').numFmt = '0';
      worksheet.getColumn('rate').numFmt = '0.00';
      worksheet.getColumn('totalCost').numFmt = '0.00';

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      saveAs(blob, `billing_records_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast({
        title: 'Success',
        description: 'Billing records exported successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error exporting Excel file:', error);
      toast({
        title: 'Error',
        description: 'Failed to export billing records',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const formatDateTime = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const fetchAllBillings = async () => {
    let allBillings = [];
    let page = 1;
    let hasMoreData = true;

    while (hasMoreData) {
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: '100'
        });

        const response = await axios.get(`/api/protected/billings?${params.toString()}`);
        const { billings, pagination } = response.data.data;

        if (billings && billings.length > 0) {
          allBillings = [...allBillings, ...billings];
          if (page >= pagination.totalPages) {
            hasMoreData = false;
          } else {
            page += 1;
          }
        } else {
          hasMoreData = false;
        }
      } catch (error) {
        console.error('Error fetching all billings:', error);
        hasMoreData = false;
      }
    }

    return allBillings;
  };

  const fetchBillings = async (page = 1, filters = {}) => {
    setLoading(true);
    try {
      // Only include non-empty filter values
      const validFilters = {};
      if (filters.startDate) validFilters.startDate = filters.startDate;
      if (filters.endDate) validFilters.endDate = filters.endDate;
      if (filters.from) validFilters.from = filters.from;
      if (filters.to) validFilters.to = filters.to;
      if (filters.callSid) validFilters.callSid = filters.callSid;

      const params = new URLSearchParams({
        page: String(page),
        limit: String(10),
        ...validFilters,
      });

      const response = await axios.get(`/api/protected/billings?${params.toString()}`);
      const { billings, pagination } = response.data.data;
      setFilteredBillings(billings);
      setTotalPages(pagination.totalPages);
      setCurrentPage(pagination.currentPage);
    } catch (error) {
      console.error('Error fetching billings:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch billing records',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    // Implement search logic
  };

  const handleFilterChange = (type, value) => {
    switch (type) {
      case 'startDate':
        setStartDate(value);
        break;
      case 'endDate':
        setEndDate(value);
        break;
      case 'fromNumber':
        setFromNumber(value);
        break;
      case 'toNumber':
        setToNumber(value);
        break;
      case 'callSid':
        setCallSid(value);
        break;
      default:
        break;
    }
  };

  const applyFilters = () => {
    // Reset to first page when applying filters
    const filters = {};
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    if (fromNumber) filters.from = fromNumber;
    if (toNumber) filters.to = toNumber;
    if (callSid) filters.callSid = callSid;

    fetchBillings(1, filters);
    setIsFilterOpen(false);
  };


  const handleViewDetails = (call) => {
    setSelectedCall(call);
    onOpen();
  };

  const handleRecordingClose = () => {
    if (recordingPlayerUrl) {
      URL.revokeObjectURL(recordingPlayerUrl);
      setRecordingPlayerUrl('');
    }

    setRecordingLoadingCallSid('');
    onRecordingClose();
  };

  const handleListenRecording = async (callSid) => {
    try {
      setRecordingLoadingCallSid(callSid);

      if (recordingPlayerUrl) {
        URL.revokeObjectURL(recordingPlayerUrl);
        setRecordingPlayerUrl('');
      }

      const response = await axios.get(`/api/protected/billings/${callSid}/recording`, {
        responseType: 'blob'
      });

      const audioUrl = URL.createObjectURL(response.data);
      setRecordingPlayerUrl(audioUrl);
      onRecordingOpen();
    } catch (error) {
      console.error('Error loading recording:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to load recording',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setRecordingLoadingCallSid('');
    }
  };

  const handleDownload = async (content, callSid) => {
    try {
      const blob = new Blob([content], { type: 'text/plain' }); // or "application/json", etc.
      const url = URL.createObjectURL(blob);
      const filename = `call_log_${callSid}.txt`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();

      URL.revokeObjectURL(url); // cleanup

      setLoading(true);

      toast({
        title: 'Success',
        description: 'Recording downloaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error downloading recording:', error);
      toast({
        title: 'Error',
        description: 'Failed to download recording',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      // Only include filters that have values
      const filters = {};
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;
      if (fromNumber) filters.from = fromNumber;
      if (toNumber) filters.to = toNumber;
      if (callSid) filters.callSid = callSid;

      fetchBillings(page, filters);
    }
  };

  const handleColumnVisibilityChange = async (columnId, isVisible) => {
    try {

      // Update local state
      if (isVisible) {
        setVisibleColumns([...visibleColumns, columnId]);
      } else {
        setVisibleColumns(visibleColumns.filter(col => col !== columnId));
      }

      toast({
        title: 'Success',
        description: 'Column settings updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating column preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to update column settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSaveColumnSettings = async () => {
    try {
      const preferences = await getPreferences();
      const updatedPreferences = {
        ...preferences,
        billingColumns: allColumns.reduce((acc, column) => {
          acc[column.id] = visibleColumns.includes(column.id);
          return acc;
        }, {})
      };
      await updatePreferences(updatedPreferences);
      toast({
        title: 'Success',
        description: 'Column settings saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsColumnSettingsOpen(false);
    } catch (error) {
      console.error('Error saving column preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save column settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const preferences = await getPreferences();
        if (preferences?.billingColumns) {
          // Convert the billingColumns Map to an array of visible column IDs
          const visibleCols = Object.entries(preferences.billingColumns)
            .filter(([_, isVisible]) => isVisible)
            .map(([columnId]) => columnId);
          const defaultColumns = getDefaultVisibleColumns();
          setVisibleColumns([
            ...visibleCols,
            ...defaultColumns.filter(columnId => !Object.prototype.hasOwnProperty.call(preferences.billingColumns, columnId))
          ]);
        } else {
          // Use default columns from shared config
          setVisibleColumns(getDefaultVisibleColumns());
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
        // Fallback to default columns from shared config
        setVisibleColumns(getDefaultVisibleColumns());
      }
    };

    loadPreferences();
  }, []);

  useEffect(() => {
    fetchBillings();
  }, []);

  return (
    <>
      <Helmet>
        <title>Usage | AI Call System</title>
        <meta name="description" content="View and manage your call usage records" />
      </Helmet>

      <Flex minH="100vh" bg={colorMode === 'light' ? 'gray.50' : 'gray.900'}>
        <Navigation />

        <Box flex="1" p={6}>
          <Flex justify="space-between" align="center" mb={6}>
            <Heading
              size="lg"
              color={colorMode === 'light' ? 'gray.800' : 'white'}
            >
              Usage
            </Heading>
            <Flex gap={2}>
              <Tooltip label="Column Settings">
                <IconButton
                  icon={<MdSettings />}
                  aria-label="Column Settings"
                  onClick={() => setIsColumnSettingsOpen(true)}
                  colorScheme="brand"
                />
              </Tooltip>
              <Tooltip label="Refresh data">
                <IconButton
                  icon={<MdRefresh />}
                  aria-label="Refresh data"
                  onClick={() => {
                    fetchBillings();
                  }}
                  colorScheme="brand"
                  isLoading={loading}
                />
              </Tooltip>
              <Tooltip label="Export to Excel">
                <IconButton
                  icon={<MdDownload />}
                  aria-label="Export to Excel"
                  onClick={exportToExcel}
                  colorScheme="brand"
                  isLoading={isExporting}
                />
              </Tooltip>
            </Flex>
          </Flex>

          {/* Filters and Search */}
          <Card bg={colorMode === 'light' ? 'white' : 'gray.800'} mb={6}>
            <CardBody>
              <Stack spacing={4}>
                <Flex gap={4} direction={{ base: 'column', md: 'row' }}>
                  <FormControl flex="1">
                    <InputGroup>
                      <InputLeftElement pointerEvents="none">
                        <MdSearch color="gray.300" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search calls..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                      />
                      {searchQuery && (
                        <InputRightElement>
                          <IconButton
                            size="sm"
                            icon={<MdClear />}
                            onClick={() => handleSearch('')}
                            aria-label="Clear search"
                          />
                        </InputRightElement>
                      )}
                    </InputGroup>
                  </FormControl>
                  <Popover
                    isOpen={isFilterOpen}
                    onClose={() => setIsFilterOpen(false)}
                    placement="bottom-end"
                  >
                    <PopoverTrigger>
                      <Button
                        leftIcon={<MdFilterList />}
                        rightIcon={<ChevronDownIcon />}
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                      >
                        Filters
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent>
                      <PopoverArrow />
                      <PopoverCloseButton />
                      <PopoverHeader>Filter Options</PopoverHeader>
                      <PopoverBody>
                        <Stack spacing={4}>
                          <FormControl>
                            <FormLabel>Date Range</FormLabel>
                            <HStack>
                              <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                              />
                              <Text>to</Text>
                              <Input
                                type="date"
                                value={endDate}
                                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                              />
                            </HStack>
                          </FormControl>
                          <FormControl>
                            <FormLabel>Phone Numbers</FormLabel>
                            <HStack>
                              <Input
                                placeholder="From"
                                value={fromNumber}
                                onChange={(e) => handleFilterChange('fromNumber', e.target.value)}
                              />
                              <Input
                                placeholder="To"
                                value={toNumber}
                                onChange={(e) => handleFilterChange('toNumber', e.target.value)}
                              />
                            </HStack>
                          </FormControl>
                          <FormControl>
                            <FormLabel>Call SID</FormLabel>
                            <Input
                              placeholder="Enter Call SID"
                              value={callSid}
                              onChange={(e) => handleFilterChange('callSid', e.target.value)}
                            />
                          </FormControl>
                        </Stack>
                      </PopoverBody>
                      <PopoverFooter>
                        <HStack spacing={2} justify="flex-end">
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setStartDate('');
                              setEndDate('');
                              setFromNumber('');
                              setToNumber('');
                              setCallSid('');
                              fetchBillings(1, {});
                              setIsFilterOpen(false);
                            }}
                          >
                            Clear All
                          </Button>
                          <Button
                            colorScheme="brand"
                            onClick={applyFilters}
                          >
                            Apply Filters
                          </Button>
                        </HStack>
                      </PopoverFooter>
                    </PopoverContent>
                  </Popover>
                </Flex>
              </Stack>
            </CardBody>
          </Card>

          {/* Billing Table */}
          <Card bg={colorMode === 'light' ? 'white' : 'gray.800'}>
            <CardBody>
              {loading ? (
                <Stack spacing={4}>
                  <Skeleton height="40px" />
                  <Skeleton height="40px" />
                  <Skeleton height="40px" />
                </Stack>
              ) : (
                <Box overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        {visibleColumns.includes('date') && (
                          <Th onClick={() => handleSort('date')} cursor="pointer">
                            Date
                            {sortConfig.key === 'date' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('callId') && (
                          <Th onClick={() => handleSort('callId')} cursor="pointer">
                            Call ID
                            {sortConfig.key === 'callId' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('assistant') && (
                          <Th>Assistant</Th>
                        )}
                        {visibleColumns.includes('from') && (
                          <Th onClick={() => handleSort('from')} cursor="pointer">
                            From
                            {sortConfig.key === 'from' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('to') && (
                          <Th onClick={() => handleSort('to')} cursor="pointer">
                            To
                            {sortConfig.key === 'to' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('duration') && (
                          <Th onClick={() => handleSort('duration')} cursor="pointer">
                            Duration
                            {sortConfig.key === 'duration' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('chargedDuration') && (
                          <Th onClick={() => handleSort('chargedDuration')} cursor="pointer">
                            Charged Duration
                            {sortConfig.key === 'chargedDuration' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('rate') && (
                          <Th onClick={() => handleSort('rate')} cursor="pointer">
                            Rate
                            {sortConfig.key === 'rate' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('totalCost') && (
                          <Th onClick={() => handleSort('totalCost')} cursor="pointer">
                            Cost
                            {sortConfig.key === 'totalCost' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('recording') && (
                          <Th>Recording</Th>
                        )}
                        {visibleColumns.includes('direction') && (
                          <Th onClick={() => handleSort('direction')} cursor="pointer">
                            Direction
                            {sortConfig.key === 'direction' && (
                              <ChevronDownIcon
                                transform={sortConfig.direction === 'asc' ? 'rotate(180deg)' : ''}
                              />
                            )}
                          </Th>
                        )}
                        {visibleColumns.includes('log') && (
                          <Th>Log</Th>
                        )}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredBillings.map((billing) => (
                        <Tr key={billing.callSid}>
                          {visibleColumns.includes('date') && (
                            <Td>{formatDateTime(billing.createdAt)}</Td>
                          )}
                          {visibleColumns.includes('callId') && (
                            <Td>{billing.callSid}</Td>
                          )}
                          {visibleColumns.includes('assistant') && (
                            <Td>
                              {getAssistantId(billing) ? (
                                <Button
                                  variant="link"
                                  colorScheme="blue"
                                  textAlign="left"
                                  whiteSpace="normal"
                                  height="auto"
                                  onClick={() => openAssistant(billing)}
                                >
                                  <VStack align="start" spacing={0}>
                                    <Text fontWeight="semibold">{getAssistantName(billing)}</Text>
                                    <Code fontSize="xs">{getAssistantId(billing)}</Code>
                                  </VStack>
                                </Button>
                              ) : (
                                <Text color={colorMode === 'light' ? 'gray.500' : 'gray.400'}>No assistant</Text>
                              )}
                            </Td>
                          )}
                          {visibleColumns.includes('from') && (
                            <Td>{billing.from}</Td>
                          )}
                          {visibleColumns.includes('to') && (
                            <Td>{billing.to}</Td>
                          )}
                          {visibleColumns.includes('duration') && (
                            <Td>{(billing.duration / 60000).toFixed(2)} mins</Td>
                          )}
                          {visibleColumns.includes('chargedDuration') && (
                            <Td>{Math.ceil(billing.chargedDuration)} mins</Td>
                          )}
                          {visibleColumns.includes('rate') && (
                            <Td>{billing.rate?.toFixed(2)} SGD/min</Td>
                          )}
                          {visibleColumns.includes('totalCost') && (
                            <Td>{formatCurrency(billing.totalCost)}</Td>
                          )}
                          {visibleColumns.includes('recording') && (
                            <Td>
                              {billing.recordingUrl ? (
                                <Button
                                  onClick={() => handleListenRecording(billing.callSid)}
                                  colorScheme="blue"
                                  variant="solid"
                                  isLoading={recordingLoadingCallSid === billing.callSid}
                                >
                                  Listen
                                </Button>
                              ) : (<Text>No Recording</Text>)}
                            </Td>

                          )}
                          {visibleColumns.includes('direction') && (
                            <Td>
                              <HStack spacing={2}>
                                {billing.direction === 'inbound' ? (
                                  <Tooltip label="Inbound Call">
                                    <Box>
                                      <Icon as={MdPhoneInTalk} color="green.500" boxSize={5} />
                                    </Box>
                                  </Tooltip>
                                ) : (
                                  <Tooltip label="Outbound Call">
                                    <Box>
                                      <Icon as={MdPhoneForwarded} color="blue.500" boxSize={5} />
                                    </Box>
                                  </Tooltip>
                                )}
                              </HStack>
                            </Td>
                          )}
                          {visibleColumns.includes('log') && (
                            <Td>
                              {billing.callLog ? (
                                <IconButton
                                  onClick={() => handleDownload(billing.callLog, billing.callSid)}
                                  icon={<MdDownload />}
                                  aria-label="Download file"
                                  colorScheme="brand"
                                />) : (<></>)}
                            </Td>
                          )}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </CardBody>
            <CardFooter>
              <Flex justify="space-between" align="center" width="100%">
                <Text>
                  Showing {filteredBillings.length} of {(totalPages * 10) || 0} records
                </Text>
                <HStack>
                  <Button
                    leftIcon={<ArrowBackIcon />}
                    onClick={() => handlePageChange(currentPage - 1)}
                    isDisabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Text mx={4}>
                    Page {currentPage} of {totalPages || 1}
                  </Text>
                  <Button
                    rightIcon={<ArrowForwardIcon />}
                    onClick={() => handlePageChange(currentPage + 1)}
                    isDisabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            </CardFooter>
          </Card>
        </Box>
      </Flex>


      {/* Column Settings Modal */}
      <Modal isOpen={isColumnSettingsOpen} onClose={() => setIsColumnSettingsOpen(false)}>
        <ModalOverlay />
        <ModalContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
          <ModalHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>Column Settings</ModalHeader>
          <ModalCloseButton color={colorMode === 'light' ? 'gray.800' : 'white'} />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {allColumns.map((column) => (
                <FormControl key={column.id}>
                  <Checkbox
                    isChecked={visibleColumns.includes(column.id)}
                    onChange={(e) => handleColumnVisibilityChange(column.id, e.target.checked)}
                    color={colorMode === 'light' ? 'gray.800' : 'white'}
                  >
                    {column.label}
                  </Checkbox>
                </FormControl>
              ))}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsColumnSettingsOpen(false)}>
              Close
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSaveColumnSettings}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isRecordingOpen} onClose={handleRecordingClose} size="lg" isCentered>
        <ModalOverlay />
        <ModalContent bg={colorMode === 'light' ? 'white' : 'gray.800'}>
          <ModalHeader color={colorMode === 'light' ? 'gray.800' : 'white'}>
            Call Recording
          </ModalHeader>
          <ModalCloseButton color={colorMode === 'light' ? 'gray.800' : 'white'} />
          <ModalBody pb={6}>
            {recordingPlayerUrl ? (
              <Box>
                <audio controls autoPlay style={{ width: '100%' }}>
                  <source src={recordingPlayerUrl} />
                  Your browser does not support audio playback.
                </audio>
              </Box>
            ) : (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BillingPage;
